/**
 * Was tut die Leinwand beim Phasenwechsel?
 *
 * Diese Entscheidung stand dreimal falsch im Effekt der PresenterPage, und
 * jedes Mal fiel es erst vor Publikum auf: Ein gesetztes `forceSlides` hielt
 * die Leinwand auf den Folien fest, während der Moderator vergeblich
 * weiterschaltete. Als reine Funktion lässt sie sich prüfen, ohne die halbe
 * Anwendung zu starten.
 */

export interface LeinwandRegeln {
  /** Phasen, in denen die Workshop-Oberfläche die Leinwand übernimmt. */
  overlayPhases: ReadonlySet<string>;
  /** Folie für die Warte-Phase (QR-Code). Weglassen = kein Sprung. */
  waitingSlide?: number;
  /** Folie für die Generierungs-Ankündigung. Weglassen = kein Sprung. */
  overnightSlide?: number;
  /**
   * Strategietag: Springt die Phase von selbst auf "reveal" (Bühne fertig),
   * bleibt die aktuelle V-Folie stehen, bis die Moderation „Ergebnisse"
   * klickt (Briefing Prompt-Teil 5). Ohne das Flag übernimmt das Overlay
   * sofort — das bisherige AR-Verhalten.
   */
  revealManuell?: boolean;
}

export interface LeinwandWechsel {
  /** Neuer Wert für die Folien-Sperre. Weglassen = unverändert lassen. */
  forceSlides?: boolean;
  /** Foliennummer, auf die gesprungen wird. Weglassen = kein Sprung. */
  index?: number;
}

export function leinwandWechsel(
  vorher: string,
  jetzt: string,
  regeln: LeinwandRegeln,
): LeinwandWechsel {
  if (vorher === jetzt) return {};

  // Generierung: Die Leinwand bleibt bei den Folien, damit der Moderator
  // weiter erzählen kann, während die Lambda arbeitet.
  if (jetzt === "overnight") {
    return regeln.overnightSlide != null
      ? { forceSlides: true, index: regeln.overnightSlide }
      : { forceSlides: true };
  }

  // Warten auf Gäste: Adresse, PIN und QR-Code gehören auf die Leinwand.
  // Die Sperre wird dabei gelöst, nicht gesetzt — sonst käme der Moderator
  // aus dieser Phase nicht mehr heraus.
  if (jetzt === "waiting" && regeln.waitingSlide != null) {
    return { forceSlides: false, index: regeln.waitingSlide };
  }

  // Der automatische Sprung Generierung → Ergebnisse lässt die Leinwand in
  // Ruhe — die Moderation erzählt gerade auf einer V-Folie weiter.
  if (regeln.revealManuell && vorher === "overnight" && jetzt === "reveal") {
    return {};
  }

  // Alles, was die Workshop-Oberfläche zeigen soll, hebt die Sperre auf.
  if (regeln.overlayPhases.has(jetzt)) {
    return { forceSlides: false };
  }

  return {};
}

/**
 * Bis zu welcher Folie darf frei geblättert werden? Immer nur bis zum
 * Horizont des erreichten Schritts (Fund 17.09.): Aus dem Thema heraus
 * landete man per Pfeiltaste sonst auf der Arbeitsphasen- oder Bau-Folie
 * mit leerer Uhr. Rückwärts ist immer alles frei.
 */
export function maxFreieFolie(
  phase: string,
  anzeigePhase: string | undefined,
  cfg: {
    themaSlide: number;
    workingSlide: number;
    vortragSlides: [number, number];
    abschlussSlide: number;
    deckLaenge: number;
  },
): number {
  switch (phase) {
    case "waiting":
    case "einfuehrung":
      return cfg.themaSlide;
    case "working":
      return anzeigePhase === "arbeitsphase" ? cfg.workingSlide : cfg.themaSlide;
    case "overnight":
    case "reveal":
      return cfg.vortragSlides[1];
    case "abschluss":
      return cfg.deckLaenge - 1;
    default:
      return cfg.abschlussSlide - 1;
  }
}
